import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPText, IPTooltipHost } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall } from 'globalStyles';
import { useContext } from 'react';
import { toLocaleDateTimeMedium } from 'utils/datetime';

interface IAuditTrailEntryCardProps {
  // Who did it: the id resolves the photo, the name is what the persona falls back to.
  userId?: string;
  userName: string;
  date: Date;
  // What happened, in the wording of the trail it belongs to.
  text: string;
  // Appended to the moment, for a trail whose actor is not simply the persona beside it (a system
  // action somebody triggered). The audit trail leaves it out: its user IS the persona.
  actor?: string;
  // One more line under the text - the properties an amendment touched, say.
  detail?: string;
}

// The card a change log entry is drawn in: persona, the moment, what happened. Every trail in the app
// renders through this one component - the entity audit trail and the form version amendments - so a
// change to how an entry looks lands in both instead of drifting apart.
export const AuditTrailEntryCard = (props: IAuditTrailEntryCardProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();

  return (
    <IPStack
      horizontal
      tokens={globalStackTokensGapSmall}
      styles={{
        root: {
          borderWidth: 1,
          borderColor: appContext.useDarkMode ? theme.palette.themeDark : theme.palette.themeLight,
          background: appContext.useDarkMode ? theme.palette.themeDarkAlt : theme.palette.themeLighterAlt,
          borderStyle: 'solid',
          borderRadius: '3px',
          marginBottom: 10,
          padding: 2,
        },
      }}
    >
      <IPStack.Item>
        <IPPersona
          imageUrl={props.userId ? appContext.globalDataCache.users.getPhotoURLObj(props.userId) : undefined}
          text={props.userName}
          hidePersonaDetails={true}
          size={IPPersonaSize.size24}
        />
      </IPStack.Item>
      <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
        <IPStack>
          <IPStack.Item>
            <IPText block nowrap variant="small">
              {props.actor
                ? `${toLocaleDateTimeMedium(props.date)} | ${props.actor}`
                : toLocaleDateTimeMedium(props.date)}
            </IPText>
          </IPStack.Item>
          <IPStack.Item>
            <IPTooltipHost content={props.text}>
              <IPText block>{props.text}</IPText>
            </IPTooltipHost>
          </IPStack.Item>
          {props.detail && (
            <IPStack.Item>
              <IPText block>{props.detail}</IPText>
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default AuditTrailEntryCard;
